// @ts-check
import React from 'react';
import { Avatar, Switch, Tooltip } from '@material-ui/core';
import _ from 'lodash';
import styled from 'styled-components';
import { Flex, Separator } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';
import { SPACES } from 'common/constants/sizes';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import { onTeamSelection } from 'common/services/teams.service';
import {
  filterAndFormatTeamSettings,
  getTeamSettings
} from 'common/services/settings.service';
import AppRouter from 'common/AppRouter';
import { LBButton } from '@linearb/buzz';

export const getTableColumns = channelNameTitle => [
  {
    name: 'STATUS',
    dataPoint: 'notificationStatus',
    tableCellProps: {
      style: { borderRight: 0, borderLeft: 0, width: '10rem' }
    }
  },
  {
    name: 'TEAM',
    dataPoint: 'name',
    tableCellProps: {
      style: { borderRight: 0, borderLeft: 0 }
    }
  },
  {
    name: channelNameTitle,
    dataPoint: 'connectionStatus'
  },
  {
    name: 'NOTIFIED ABOUT',
    dataPoint: 'teamNotifiedAbout',
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    name: '',
    dataPoint: 'editNotifications',
    tableCellProps: {
      style: { borderRight: 0, borderLeft: 0, textAlign: 'right' }
    }
  }
];

export const getTableData = props => {
  const {
    hasTeams,
    isOrgSettings,
    currentTeams,
    settings,
    currentTeam,
    // each application should implement its own function of createTeamData
    createTeamData,
    uiState
  } = props;
  if (!hasTeams) {
    return [];
  }

  if (isOrgSettings) {
    return currentTeams
      .map(team => createTeamData(team, settings ? settings[team.id] : null))
      .filter(Boolean);
  } else if (uiState.selectedTeamId) {
    return [
      createTeamData(currentTeam, settings ? settings[currentTeam.id] : null)
    ];
  }
  return [];
};

export const getNotificationNameColumn = team => (
  <Flex direction='row' items='center'>
    <Avatar alt='' style={{ backgroundColor: team.color }}>
      <Text weight='bold' color='white'>
        {team.initials}
      </Text>
    </Avatar>
    <Tooltip title={team.name}>
      <Text size='large' pl={SPACES.small} ellipsisAfter='20rem'>
        {team.name}
      </Text>
    </Tooltip>
  </Flex>
);

export const getNotificationStatusColumn = (
  channel,
  sensorAuhorization,
  isNotificationsChecked,
  team,
  toggleNotificationsForTeamWrapper
) => (
  <Flex items='center'>
    <Switch
      onChange={event =>
        toggleNotificationsForTeamWrapper(team, event.target.checked)
      }
      checked={!!isNotificationsChecked}
      disabled={!channel || !sensorAuhorization}
    />
    <Text size='medium' pr={SPACES.small}>
      {isNotificationsChecked ? 'ON' : 'OFF'}
    </Text>
  </Flex>
);

export const getNotificationConnectionStatusColumn = (
  channelName,
  sensorAuhorization,
  team,
  settings,
  openChannelModal,
  getConnectToChatToolButton,
  isEditable = false
) =>
  channelName && sensorAuhorization ? (
    <Flex items='center'>
      <Tooltip title={channelName}>
        <Text
          size='medium'
          color='shuttleGray'
          pr={SPACES.small}
          ellipsisAfter='25rem'
        >
          {channelName}
        </Text>
      </Tooltip>
      {isEditable && (
        <LBButton
          variant='outline'
          size='sm'
          onClick={() => openChannelModal(team, settings[team.id], true)}
        >
          <Icon name='pencil' size='x-small' />
        </LBButton>
      )}
    </Flex>
  ) : (
    getConnectToChatToolButton(team)
  );

export const getNotificationNotifiedAboutColumn = (
  isNotificationsChecked,
  notifications
) => {
  const numberOfNotifications =
    notifications.filter(({ enabled }) => enabled).length - 1;
  return isNotificationsChecked && notifications.length ? (
    <Flex items='center'>
      {notifications
        .filter(({ enabled }) => enabled)
        .map(({ name }, index) => {
          const isLast = index === numberOfNotifications;
          return (
            <React.Fragment key={name}>
              <Text size='small' color='shuttleGray'>
                {name}
              </Text>
              {!isLast ? <Separator margin='0 0.7rem' /> : null}
            </React.Fragment>
          );
        })}
    </Flex>
  ) : null;
};

const getNotificationEditButton = (
  isNotificationsChecked,
  team,
  selectTeamId,
  history,
  isTeamLicense
) =>
  isNotificationsChecked ? (
    <LBButton
      variant='outline'
      size='sm'
      onClick={() => {
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.ORG_SLACK,
          click_type: TRACKING.CLICK.EDIT_NOTIFICATIONS
        });

        onTeamSelection({
          team,
          reduxActionFn: selectTeamId,
          history,
          isTeamLicense
        });

        history.push({
          pathname: AppRouter.NOTIFICATIONS.path,
          search: `?redirectToPath=true`
        });
      }}
    >
      Edit notifications
      <Icon name='pencil' size='x-small' />{' '}
    </LBButton>
  ) : null;

const getNotificationUnlinkButton = (
  isNotificationsChecked,
  team,
  unlinkTeam
) =>
  isNotificationsChecked ? (
    <LBButton
      variant='outline'
      size='sm'
      onClick={() => {
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.ORG_SLACK,
          click_type: TRACKING.CLICK.EDIT_NOTIFICATIONS
        });
        unlinkTeam(team);
      }}
    >
      Unlink
    </LBButton>
  ) : null;

export const createTeamData = (
  team,
  teamSettings,
  sensorAuhorization,
  settings,
  selectTeamId,
  history,
  channel,
  channelName,
  isChannelEditable,
  // functions
  toggleNotificationsForTeamWrapper,
  openChannelModal,
  getConnectToChatToolButton,
  unlinkTeam,
  isTeamLicense
) => {
  const { isNotificationEnabled = false, notifications = [] } =
    teamSettings || {};

  const isNotificationsChecked =
    sensorAuhorization && isNotificationEnabled && channel;

  const name = getNotificationNameColumn(team);

  const notificationStatus = getNotificationStatusColumn(
    channel,
    sensorAuhorization,
    isNotificationsChecked,
    team,
    toggleNotificationsForTeamWrapper
  );

  const connectionStatus = getNotificationConnectionStatusColumn(
    channelName,
    sensorAuhorization,
    team,
    settings,
    openChannelModal,
    getConnectToChatToolButton,
    isChannelEditable
  );

  const teamNotifiedAbout = getNotificationNotifiedAboutColumn(
    isNotificationsChecked,
    notifications
  );

  const editNotifications = getNotificationEditButton(
    isNotificationsChecked,
    team,
    selectTeamId,
    history,
    isTeamLicense
  );

  const unlinkNotifications = !!unlinkTeam
    ? getNotificationUnlinkButton(isNotificationsChecked, team, unlinkTeam)
    : null;

  return {
    name,
    notificationStatus,
    connectionStatus,
    teamNotifiedAbout,
    editNotifications,
    unlinkNotifications
  };
};

export const loadTeamsSettings = async currentTeams => {
  const teamsSettings = await Promise.all(
    currentTeams.map(({ id }) =>
      getTeamSettings(id).then(response => ({ ...response, teamId: id }))
    )
  );
  return _.keyBy(
    teamsSettings.map(({ notifications, teamId }) => {
      return filterAndFormatTeamSettings(notifications, teamId);
    }),
    'teamId'
  );
};

export const TableWrapper = styled.div`
  .MuiTableHead-root > .MuiTableRow-head:first-child {
    border-top: 0;
  }

  .MuiTableHead-root .MuiTableCell-head {
    border-left: 0;
    border-right: 0;
  }
`;
